import styled from 'styled-components';
import { Link } from 'react-router-dom';

/**
 * Dev dashboard uses dark theme. These components mirror CoachDashboard
 * but with dark CSS variable overrides applied via .dev-dashboard wrapper.
 */
export const DevDashboardWrapper = styled.div`
  --bg-primary: #1a1a1e;
  --bg-sidebar: #121216;
  --bg-tertiary: #2a2a30;
  --text-dark: #f0f0f0;
  --text-gray: #b0b0b8;
  --text-primary: #e8e8ec;
  --text-secondary: #e0e0e6;
  --text-muted: #8888a0;
  --card-bg: #222228;
  --card-border: #333340;
  --card-hover: #2a2a32;
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.3);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.4);
  min-height: 100vh;
  background: var(--bg-primary);
`;

export const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
`;

export const BurgerButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 101;
  background: var(--accent-orange);
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  cursor: pointer;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: 0;
  box-shadow: var(--shadow-md);
  transition: all 0.2s;

  &:hover {
    background: var(--accent-orange-dark);
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  span {
    width: 24px;
    height: 3px;
    background: #fff;
    border-radius: 2px;
    transition: all 0.3s;
    transform-origin: center;

    &:nth-child(1) {
      transform: ${(props) => (props.$isOpen ? 'rotate(45deg) translateY(8px)' : 'none')};
    }
    &:nth-child(2) {
      opacity: ${(props) => (props.$isOpen ? 0 : 1)};
    }
    &:nth-child(3) {
      transform: ${(props) => (props.$isOpen ? 'rotate(-45deg) translateY(-8px)' : 'none')};
    }
  }
`;

export const SidebarOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 99;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s, visibility 0.3s;

  @media (max-width: 768px) {
    display: block;
  }
`;

export const Sidebar = styled.aside<{ $isOpen: boolean }>`
  width: 320px;
  background: var(--bg-sidebar);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  position: fixed;
  top: 0;
  left: 0;
  box-shadow: var(--shadow-lg);
  z-index: 100;
  height: 100vh;
  overflow-y: auto;
  transition: transform 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 260px;
    transform: ${(props) => (props.$isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  }
`;

export const SidebarTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem 0 0 0;
`;

export const SidebarLogoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  @media (min-width: 769px) {
    font-size: 2.5rem;
  }
`;

export const SidebarLogoLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  transition: opacity 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    opacity: 0.85;
  }

  @media (min-width: 769px) {
    font-size: 2.5rem;
  }
`;

export const SidebarLogoSJ = styled.span`
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  letter-spacing: -0.05em;
`;

export const SidebarLogoMate = styled.span`
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  letter-spacing: -0.05em;
`;

export const SidebarTitle = styled.div`
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.15em;
  margin-top: 0.2rem;
  text-align: center;
  text-transform: uppercase;
`;

export const SidebarDivider = styled.div`
  width: 85%;
  height: 4px;
  background: var(--accent-orange);
  margin: 1rem auto;
`;

export const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  padding: 0;
  margin-top: 0.5rem;
  flex: 1;
  justify-content: flex-start;
`;

export const NavItem = styled.button<{ $active?: boolean }>`
  width: 90%;
  background: none;
  border: none;
  color: ${(props) => (props.$active ? 'var(--accent-orange)' : 'var(--text-gray)')};
  font-family: 'Poppins', sans-serif;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.45rem 1.2rem 0.45rem 2.2rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
  box-shadow: none;
  position: relative;
  margin: 0 auto;
  flex-shrink: 0;

  &:hover,
  &:focus {
    color: var(--accent-orange);
    outline: none;
    box-shadow: none;
  }

  &::before {
    content: '';
    display: ${(props) => (props.$active ? 'block' : 'none')};
    position: absolute;
    left: 1rem;
    top: 0;
    width: 5px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

export const SidebarBottom = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
  margin-top: auto;
  padding: 0 5%;
`;

export const LogoutButton = styled.button`
  flex: 1;
  background: var(--accent-orange);
  border: none;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.6rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.4rem 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  outline: none;
  border-radius: 8px;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: var(--accent-orange-dark);
    color: #fff;
  }
`;

export const MainContent = styled.main`
  flex: 1;
  margin-left: 320px;
  padding: 2rem 2.5rem;
  overflow-y: auto;
  background: var(--bg-primary);
  position: relative;
  min-height: 100vh;

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 1rem;
    padding-top: 4rem;
  }
`;

export const PageTitle = styled.h1`
  font-size: 2rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 0.5rem;
  text-align: left;
  letter-spacing: -0.02em;
`;

export const PageDescription = styled.p`
  font-size: 0.95rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1.5rem;
`;

export const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-md);
`;

export const CardTitle = styled.h2`
  font-size: 1.1rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 1rem;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const StatCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
`;

export const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 900;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
`;

export const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  color: var(--text-gray);

  th {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 2px solid var(--card-border);
    color: var(--text-dark);
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid var(--card-border);
  }

  tr:hover td {
    background: var(--card-hover);
  }
`;

export const ErrorMessage = styled.div`
  color: #ff6b6b;
  padding: 1rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
`;

export const LoadingMessage = styled.div`
  color: var(--text-gray);
  padding: 2rem;
  text-align: center;
  font-family: 'Poppins', sans-serif;
`;

export const PlanTag = styled.span<{ $plan?: string }>`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${(props) => {
    const p = (props.$plan || 'free').toLowerCase();
    if (p === 'hs240') return 'rgba(255, 150, 0, 0.25)';
    if (p === 'hs130') return 'rgba(74, 158, 255, 0.25)';
    if (p === 'hs90') return 'rgba(52, 199, 119, 0.25)';
    if (p === 'hs60') return 'rgba(168, 85, 247, 0.25)';
    return 'var(--card-border)';
  }};
  color: ${(props) => {
    const p = (props.$plan || 'free').toLowerCase();
    if (p === 'hs240') return 'var(--accent-orange)';
    if (p === 'hs130') return '#4a9eff';
    if (p === 'hs90') return '#34c777';
    if (p === 'hs60') return '#a855f7';
    return 'var(--text-gray)';
  }};
  border: 1px solid ${(props) => {
    const p = (props.$plan || 'free').toLowerCase();
    if (p === 'hs240') return 'var(--accent-orange)';
    if (p === 'hs130') return '#4a9eff';
    if (p === 'hs90') return '#34c777';
    if (p === 'hs60') return '#a855f7';
    return 'var(--card-border)';
  }};
`;

export const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ChartsSideBySide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (min-width: 769px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: start;
  }
`;

export const ChartContainer = styled.div`
  width: 100%;
  min-height: 240px;
  font-family: 'Poppins', sans-serif;

  .recharts-legend-item-text {
    color: var(--text-gray) !important;
  }
  .recharts-tooltip-wrapper {
    outline: none;
  }
`;

export const CountryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;

  li {
    padding: 0.4rem 0;
    border-bottom: 1px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }
  li:last-child {
    border-bottom: none;
  }
`;

export const SubscriptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const PlanCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
`;

export const PlanName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  margin-bottom: 0.5rem;
`;

export const PlanCount = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
`;

export const PlanMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  margin-top: 0.5rem;
`;
